import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Lock, Check, Sparkles, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { MOOD_OPTIONS, type MoodOption } from '@/types/mood';
import { useLanguage } from '@/i18n/LanguageContext';
import { toast } from 'sonner';
import { MoodShareDialog } from './MoodShareDialog';

const MAX_NOTES_LENGTH = 500;

interface MoodMeterProps {
  onSave: (moodValue: number, moodLabel: string, isPublic: boolean, notes?: string) => Promise<void>;
  currentMood?: { value: number; label: string; isPublic: boolean; notes?: string; createdAt?: string } | null;
  isLoading?: boolean;
}

export function MoodMeter({ onSave, currentMood, isLoading }: MoodMeterProps) {
  const { language } = useLanguage();
  const [selectedMood, setSelectedMood] = useState<MoodOption | null>(
    currentMood ? MOOD_OPTIONS.find((m) => m.value === currentMood.value) || null : null
  );
  const [isPublic, setIsPublic] = useState(currentMood?.isPublic || false);
  const [notes, setNotes] = useState(currentMood?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const content = {
    en: {
      title: 'How are you feeling?',
      subtitle: 'Take a moment to check in with yourself',
      shareLabel: 'Share with others',
      privateLabel: 'Keep private',
      notesPlaceholder: 'Add a note about how you\'re feeling... (optional)',
      save: 'Save My Mood',
      saved: 'Mood saved!',
      updating: 'Update mood',
      sendMood: 'Send to Someone',
    },
    es: {
      title: '¿Cómo te sientes?',
      subtitle: 'Tómate un momento para reflexionar',
      shareLabel: 'Compartir con otros',
      privateLabel: 'Mantener privado',
      notesPlaceholder: 'Añade una nota sobre cómo te sientes... (opcional)',
      save: 'Guardar Mi Estado',
      saved: '¡Estado guardado!',
      updating: 'Actualizar estado',
      sendMood: 'Enviar a Alguien',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const handleSave = async () => {
    if (!selectedMood) return;

    // Sanitize notes before saving
    const sanitizedNotes = notes.trim().substring(0, MAX_NOTES_LENGTH);

    setIsSaving(true);
    try {
      await onSave(selectedMood.value, selectedMood.label, isPublic, sanitizedNotes || undefined);
      toast.success(t.saved);
    } catch (error) {
      toast.error('Failed to save mood');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanged =
    selectedMood &&
    (selectedMood.value !== currentMood?.value || 
     isPublic !== currentMood?.isPublic ||
     notes.trim() !== (currentMood?.notes || ''));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border border-border/50 p-6"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-2xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-accent/10 to-transparent rounded-full blur-2xl" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">{t.title}</h3>
            <p className="text-sm text-muted-foreground">{t.subtitle}</p>
          </div>
        </div>

        {/* Mood Options */}
        <div className="flex justify-between gap-2 mb-6">
          {MOOD_OPTIONS.map((mood, index) => (
            <motion.button
              key={mood.value}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              onClick={() => setSelectedMood(mood)}
              className={cn(
                'flex-1 flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200',
                'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50',
                selectedMood?.value === mood.value
                  ? `${mood.bgColor} ring-2 ring-offset-2 ring-offset-background ${mood.color.replace('text-', 'ring-')}`
                  : 'bg-background/50 hover:bg-background/80'
              )}
            >
              <span className="text-2xl md:text-3xl">{mood.emoji}</span>
              <span
                className={cn(
                  'text-xs font-medium',
                  selectedMood?.value === mood.value ? mood.color : 'text-muted-foreground'
                )}
              >
                {mood.label}
              </span>
              {selectedMood?.value === mood.value && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={cn('absolute -top-1 -right-1', mood.color)}
                >
                  <Check className="h-4 w-4" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Privacy Toggle */}
        <AnimatePresence>
          {selectedMood && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              {/* Notes Input */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>{notes.length}/{MAX_NOTES_LENGTH}</span>
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.substring(0, MAX_NOTES_LENGTH))}
                  placeholder={t.notesPlaceholder}
                  className="resize-none bg-background/50 border-border/50 min-h-[80px]"
                  maxLength={MAX_NOTES_LENGTH}
                />
              </div>

              {/* Privacy Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <div className="flex items-center gap-2">
                  {isPublic ? (
                    <Share2 className="h-4 w-4 text-primary" />
                  ) : (
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Label htmlFor="mood-public" className="text-sm cursor-pointer">
                    {isPublic ? t.shareLabel : t.privateLabel}
                  </Label>
                </div>
                <Switch
                  id="mood-public"
                  checked={isPublic}
                  onCheckedChange={setIsPublic}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={isSaving || isLoading || !hasChanged}
                  className="flex-1 gap-2"
                >
                  {isSaving ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Sparkles className="h-4 w-4" />
                    </motion.div>
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {currentMood ? t.updating : t.save}
                </Button>

                {/* Send Button - only show if there's a saved mood */}
                {currentMood && (
                  <Button
                    onClick={() => setShareDialogOpen(true)}
                    variant="outline"
                    className="gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {t.sendMood}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Share Dialog */}
      {currentMood && (
        <MoodShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          moodValue={currentMood.value}
          moodLabel={currentMood.label}
          notes={currentMood.notes}
          createdAt={currentMood.createdAt || new Date().toISOString()}
        />
      )}
    </motion.div>
  );
}
